import React, { useState } from 'react';
import { useCourses } from '../../contexts/CoursesContext';
import { Course } from '../../api/courses';
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from "../../components/ui/dialog";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from "../../components/ui/select";
import { Textarea } from "../../components/ui/textarea";
import { useToast } from "../../hooks/use-toast";
import { Loader2 } from "lucide-react";

interface AddCourseProps {
    open: boolean;
    onOpenChange: (open: boolean) => void;
    courseToEdit?: Course | null;
}

const AddCourse: React.FC<AddCourseProps> = ({ open, onOpenChange, courseToEdit }) => {
    const { addCourse, updateCourse } = useCourses();
    const { toast } = useToast();
    const [isSubmitting, setIsSubmitting] = useState(false);

    const [formData, setFormData] = useState({
        name: '',
        description: '',
        credits: 3,
        hours: 45,
        semester: [] as string[],
        year: '' as '1' | '2' | '3' | '4' | '',
        status: 'actif' as 'actif' | 'inactif' | 'archive',
    });

    React.useEffect(() => {
        if (courseToEdit) {
            setFormData({
                name: courseToEdit.name,
                description: courseToEdit.description || '',
                credits: courseToEdit.credits,
                hours: courseToEdit.hours,
                semester: courseToEdit.semester,
                year: courseToEdit.year,
                status: courseToEdit.status,
            });
        } else {
            setFormData({
                name: '',
                description: '',
                credits: 3,
                hours: 45,
                semester: [],
                year: '',
                status: 'actif',
            });
        }
    }, [courseToEdit, open]);

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();

        if (!formData.name || formData.semester.length === 0 || !formData.year) {
            toast({
                title: "Erreur",
                description: "Veuillez remplir tous les champs obligatoires",
                variant: "destructive",
            });
            return;
        }

        setIsSubmitting(true);

        try {
            if (courseToEdit) {
                await updateCourse(courseToEdit.id, {
                    name: formData.name,
                    description: formData.description || undefined,
                    credits: formData.credits,
                    hours: formData.hours,
                    semester: formData.semester,
                    year: formData.year as '1' | '2' | '3' | '4',
                    status: formData.status,
                });
                toast({
                    title: "Succès",
                    description: "Le cours a été modifié avec succès",
                });
            } else {
                await addCourse({
                    name: formData.name,
                    description: formData.description || undefined,
                    credits: formData.credits,
                    hours: formData.hours,
                    semester: formData.semester,
                    year: formData.year as '1' | '2' | '3' | '4',
                    status: formData.status,
                });
                toast({
                    title: "Succès",
                    description: "Le cours a été ajouté avec succès",
                });
            }

            onOpenChange(false);
        } catch (error: any) {
            toast({
                title: "Erreur",
                description: error.message || `Une erreur est survenue lors de ${courseToEdit ? 'la modification' : "l'ajout"} du cours`,
                variant: "destructive",
            });
        } finally {
            setIsSubmitting(false);
        }
    };

    const handleSemesterChange = (sem: string) => {
        setFormData(prev => ({
            ...prev,
            semester: prev.semester.includes(sem)
                ? prev.semester.filter(s => s !== sem)
                : [...prev.semester, sem]
        }));
    };

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
                <DialogHeader>
                    <DialogTitle>{courseToEdit ? 'Modifier le cours' : 'Ajouter un nouveau cours'}</DialogTitle>
                    <DialogDescription>
                        {courseToEdit ? 'Modifiez les informations du cours.' : 'Remplissez les informations du cours. Le code sera généré automatiquement.'}
                    </DialogDescription>
                </DialogHeader>

                <form onSubmit={handleSubmit} className="space-y-4">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        {/* Name */}
                        <div className="space-y-2 md:col-span-2">
                            <Label htmlFor="name">Nom du cours *</Label>
                            <Input
                                id="name"
                                value={formData.name}
                                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                                placeholder="Biologie Médicale Fondamentale"
                                required
                            />
                        </div>

                        {/* Credits */}
                        <div className="space-y-2">
                            <Label htmlFor="credits">Crédits *</Label>
                            <Input
                                id="credits"
                                type="number"
                                min="1"
                                max="10"
                                value={formData.credits}
                                onChange={(e) => setFormData({ ...formData, credits: parseInt(e.target.value) })}
                                required
                            />
                        </div>

                        {/* Hours */}
                        <div className="space-y-2">
                            <Label htmlFor="hours">Heures totales *</Label>
                            <Input
                                id="hours"
                                type="number"
                                min="1"
                                value={formData.hours}
                                onChange={(e) => setFormData({ ...formData, hours: parseInt(e.target.value) })}
                                required
                            />
                        </div>

                        {/* Semester - Checkboxes */}
                        <div className="space-y-2">
                            <Label>Semestre(s) *</Label>
                            <div className="flex gap-4 pt-2">
                                <div className="flex items-center space-x-2">
                                    <input
                                        type="checkbox"
                                        id="sem1"
                                        checked={formData.semester.includes('1')}
                                        onChange={() => handleSemesterChange('1')}
                                        className="h-4 w-4 rounded border-gray-300"
                                    />
                                    <Label htmlFor="sem1" className="font-normal cursor-pointer">
                                        1er semestre
                                    </Label>
                                </div>
                                <div className="flex items-center space-x-2">
                                    <input
                                        type="checkbox"
                                        id="sem2"
                                        checked={formData.semester.includes('2')}
                                        onChange={() => handleSemesterChange('2')}
                                        className="h-4 w-4 rounded border-gray-300"
                                    />
                                    <Label htmlFor="sem2" className="font-normal cursor-pointer">
                                        2ème semestre
                                    </Label>
                                </div>
                            </div>
                        </div>

                        {/* Year */}
                        <div className="space-y-2">
                            <Label htmlFor="year">Année *</Label>
                            <Select value={formData.year} onValueChange={(value) => setFormData({ ...formData, year: value as '1' | '2' | '3' | '4' })}>
                                <SelectTrigger>
                                    <SelectValue placeholder="Sélectionner" />
                                </SelectTrigger>
                                <SelectContent>
                                    <SelectItem value="1">1ère année</SelectItem>
                                    <SelectItem value="2">2ème année</SelectItem>
                                    <SelectItem value="3">3ème année</SelectItem>
                                    <SelectItem value="4">4ème année</SelectItem>
                                </SelectContent>
                            </Select>
                        </div>

                        {/* Status */}
                        <div className="space-y-2">
                            <Label htmlFor="status">Statut</Label>
                            <Select value={formData.status} onValueChange={(value) => setFormData({ ...formData, status: value as any })}>
                                <SelectTrigger>
                                    <SelectValue />
                                </SelectTrigger>
                                <SelectContent>
                                    <SelectItem value="actif">Actif</SelectItem>
                                    <SelectItem value="inactif">Inactif</SelectItem>
                                    <SelectItem value="archive">Archivé</SelectItem>
                                </SelectContent>
                            </Select>
                        </div>
                    </div>

                    {/* Description */}
                    <div className="space-y-2">
                        <Label htmlFor="description">Description</Label>
                        <Textarea
                            id="description"
                            value={formData.description}
                            onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                            placeholder="Description détaillée du cours..."
                            rows={3}
                        />
                    </div>

                    <DialogFooter>
                        <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
                            Annuler
                        </Button>
                        <Button type="submit" disabled={isSubmitting}>
                            {isSubmitting && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                            {courseToEdit ? 'Modifier' : 'Ajouter'}
                        </Button>
                    </DialogFooter>
                </form>
            </DialogContent>
        </Dialog>
    );
};

export default AddCourse;
